import type { TtLocationExtendedFragment, TtLocationFragment } from '#graphql-operations'

// ─── Public Types ─────────────────────────────────────────────────────────────

export type TimeSlot = {
  open: string
  close: string
}

export type OpeningHoursExceptionDate = {
  dateString: string // YYYY-MM-DD
  dayOfMonth: number
  monthName: string
  monthNameShort: string
}

export type OpeningHoursException = {
  startDate: OpeningHoursExceptionDate
  endDate: OpeningHoursExceptionDate
  isOpen: boolean
  reason?: string | null
  slots: TimeSlot[]
}

export type OpeningHoursDay = {
  date: Date
  dateString: string // YYYY-MM-DD
  dayOfWeek: number // 0 = Sunday, 1 = Monday, ...
  dayOfMonth: number // 1–31
  dayName: string // e.g. "maandag"
  dayNameShort: string // e.g. "ma"
  monthName: string // e.g. "april"
  monthNameShort: string // e.g. "apr"
  isOpen: boolean
  isException: boolean
  exceptionReason?: string | null
  slots: TimeSlot[]
}

// ─── Internal Types ───────────────────────────────────────────────────────────

type OpeningHour = {
  day?: string | number | null
  open?: boolean | null
  openTime1?: string | null
  closeTime1?: string | null
  openTime2?: string | null
  closeTime2?: string | null
  openTime3?: string | null
  closeTime3?: string | null
}

type OpeningHourException = {
  startDate?: string | null
  endDate?: string | null
  open?: boolean | null
  reason?: string | null
  startTime1?: string | null
  endTime1?: string | null
  openTime1?: string | null
  closeTime1?: string | null
  startTime2?: string | null
  endTime2?: string | null
  openTime2?: string | null
  closeTime2?: string | null
  startTime3?: string | null
  endTime3?: string | null
  openTime3?: string | null
  closeTime3?: string | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DAY_NAMES: string[] = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag']
const DAY_NAMES_SHORT: string[] = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za']
const MONTH_NAMES: string[] = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december']
const MONTH_NAMES_SHORT: string[] = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

// ─── Helpers ──────────────────────────────────────────────────────────────────

function lookup(arr: string[], index: number): string {
  return arr[index] ?? ''
}

function parseJson<T>(value: unknown): T | null {
  if (!value) return null
  if (typeof value === 'object') return value as T
  try {
    return JSON.parse(value as string) as T
  }
  catch {
    return null
  }
}

function toDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ISO weekday: 1=Mon ... 7=Sun → JS getDay(): 0=Sun, 1=Mon ... 6=Sat
function isoWeekdayToJsDow(isoDay: number): number {
  return isoDay === 7 ? 0 : isoDay
}

function parseSlots(
  open: boolean | null | undefined,
  times: Pick<OpeningHour, 'openTime1' | 'closeTime1' | 'openTime2' | 'closeTime2' | 'openTime3' | 'closeTime3'>,
): TimeSlot[] {
  if (!open) return []
  const pairs: [string | null | undefined, string | null | undefined][] = [
    [times.openTime1, times.closeTime1],
    [times.openTime2, times.closeTime2],
    [times.openTime3, times.closeTime3],
  ]
  return pairs.filter(([o, c]) => o && c).map(([o, c]) => ({ open: o!, close: c! }))
}

function toExceptionDate(dateString: string): OpeningHoursExceptionDate {
  const d = new Date(dateString + 'T00:00:00')
  return {
    dateString,
    dayOfMonth: d.getDate(),
    monthName: lookup(MONTH_NAMES, d.getMonth()),
    monthNameShort: lookup(MONTH_NAMES_SHORT, d.getMonth()),
  }
}

function findException(exceptions: OpeningHourException[], dateString: string): OpeningHourException | undefined {
  return exceptions.find((ex) => {
    const start = ex?.startDate?.slice(0, 10)
    const end = ex?.endDate?.slice(0, 10)
    if (!start) return false
    return dateString >= start && dateString <= (end ?? start)
  })
}

// ─── Class ────────────────────────────────────────────────────────────────────

export type DateRange = { start?: Date, end?: Date } | null

export class LocationOpeningHours {
  /** The raw location fragment. */
  readonly location: TtLocationFragment | null | undefined

  readonly warehouses: TtLocationExtendedFragment[]

  /** All opening-hour days within the requested range. */
  readonly days: OpeningHoursDay[]

  /** All exceptions (sorted by start date). */
  readonly exceptions: OpeningHoursException[]

  /** The opening-hour day entry for today, if within the range. */
  readonly today: OpeningHoursDay | undefined

  constructor(
    location: TtLocationExtendedFragment | TtLocationFragment | null | undefined,
    range?: DateRange,
  ) {
    this.location = location
    this.days = this._buildDays(location, range)
    this.exceptions = this._buildExceptions(location)
    this.today = this.days.find(d => d.dateString === toDateString(new Date()))

    this.warehouses = location?.warehouses?.map((warehouse: TtLocationExtendedFragment) => new LocationOpeningHours(warehouse, range)) || []
  }

  private _buildDays(loc: TtLocationFragment | null | undefined, range?: DateRange): OpeningHoursDay[] {
    if (!loc) return []

    const now = new Date()
    const start = range?.start ?? now
    const end = range?.end ?? new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14)

    const openingHours: OpeningHour[] = (loc.openingHours ?? [])
      .map(item => parseJson<OpeningHour>(item))
      .filter(Boolean) as OpeningHour[]

    const exceptionList: OpeningHourException[] = (loc.openingHoursExceptions ?? [])
      .map(item => parseJson<OpeningHourException>(item))
      .filter(Boolean) as OpeningHourException[]

    const regularByDow = new Map<number, OpeningHour>()
    for (const oh of openingHours) {
      if (oh?.day == null) continue
      const isoDay = parseInt(String(oh.day), 10)
      if (isNaN(isoDay)) continue
      regularByDow.set(isoWeekdayToJsDow(isoDay), oh)
    }

    const result: OpeningHoursDay[] = []
    const cursor = new Date(start)
    cursor.setHours(0, 0, 0, 0)
    const endNorm = new Date(end)
    endNorm.setHours(23, 59, 59, 999)

    while (cursor <= endNorm) {
      const dateString = toDateString(cursor)
      const dow = cursor.getDay()
      const exception = findException(exceptionList, dateString)

      const base = {
        date: new Date(cursor),
        dateString,
        dayOfWeek: dow,
        dayOfMonth: cursor.getDate(),
        dayName: lookup(DAY_NAMES, dow),
        dayNameShort: lookup(DAY_NAMES_SHORT, dow),
        monthName: lookup(MONTH_NAMES, cursor.getMonth()),
        monthNameShort: lookup(MONTH_NAMES_SHORT, cursor.getMonth()),
      }

      if (exception) {
        result.push({
          ...base,
          isOpen: exception.open ?? false,
          isException: true,
          exceptionReason: exception.reason,
          slots: parseSlots(exception.open, {
            openTime1: exception.startTime1 ?? exception.openTime1,
            closeTime1: exception.endTime1 ?? exception.closeTime1,
            openTime2: exception.startTime2 ?? exception.openTime2,
            closeTime2: exception.endTime2 ?? exception.closeTime2,
            openTime3: exception.startTime3 ?? exception.openTime3,
            closeTime3: exception.endTime3 ?? exception.closeTime3,
          }),
        })
      }
      else {
        const regular = regularByDow.get(dow)
        result.push({
          ...base,
          isOpen: regular?.open ?? false,
          isException: false,
          slots: regular ? parseSlots(regular.open, regular) : [],
        })
      }

      cursor.setDate(cursor.getDate() + 1)
    }

    return result
  }

  private _buildExceptions(loc: TtLocationFragment | null | undefined): OpeningHoursException[] {
    if (!loc) return []

    const rawExceptions: OpeningHourException[] = (loc.openingHoursExceptions ?? [])
      .map(item => parseJson<OpeningHourException>(item))
      .filter(Boolean) as OpeningHourException[]

    return rawExceptions
      .filter(ex => ex?.startDate)
      .map((ex) => {
        const startDate = ex.startDate!.slice(0, 10)
        const endDate = (ex.endDate ?? ex.startDate)!.slice(0, 10)
        return {
          startDate: toExceptionDate(startDate),
          endDate: toExceptionDate(endDate),
          isOpen: ex.open ?? false,
          reason: ex.reason,
          slots: parseSlots(ex.open, {
            openTime1: ex.startTime1 ?? ex.openTime1,
            closeTime1: ex.endTime1 ?? ex.closeTime1,
            openTime2: ex.startTime2 ?? ex.openTime2,
            closeTime2: ex.endTime2 ?? ex.closeTime2,
            openTime3: ex.startTime3 ?? ex.openTime3,
            closeTime3: ex.endTime3 ?? ex.closeTime3,
          }),
        }
      })
      .sort((a, b) => a.startDate.dateString.localeCompare(b.startDate.dateString))
  }

  /**
   * Create a reactive list of `LocationOpeningHours` instances from a list of
   * location fragments. Useful for mapping over a store/location list.
   *
   * @example
   * const instances = LocationOpeningHours.fromLocations(locations)
   * // instances.value[0].today?.isOpen
   */
  static fromLocations(
    locationsRef: MaybeRef<TtLocationFragment[] | null | undefined>,
    range?: MaybeRef<DateRange>,
  ): LocationOpeningHours[] {
    return (toValue(locationsRef) ?? []).map(loc => new LocationOpeningHours(loc, toValue(range)))
  }
}
